import { Image, Page, Text, View } from '@react-pdf/renderer'
import { Button, Form, Input, message, Select } from 'antd'
import dayjs from 'dayjs'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { CoverSectionData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import GeckoLogo from 'pages/reporting/shared/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { formatDate } from 'pages/reporting/shared/pdfGeneration/utils/formatters'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useCallback, useMemo } from 'react'

const requiredCoverFields = ['asset', 'assetId', 'inspectionType']

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, selectedVisualInspection } = useVisualReportOps()
  const signoff = useMemo(() => formData.signoff, [formData.signoff])
  const data = useMemo(
    () => formData.coverSectionData,
    [formData.coverSectionData]
  )

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  // Get inspection summary from Cantilever data
  const inspectionSummary = useMemo(() => {
    if (!selectedVisualInspection) return null

    // Extract completion date
    const completionDate = selectedVisualInspection.data.date
      ? formatDate(dayjs(selectedVisualInspection.data.date))
      : 'N/A'

    return {
      company: 'Gecko Robotics',
      inspectionType: data.inspectionType,
      completionDate,
      summary: data.inspectionSummary || '',
    }
  }, [selectedVisualInspection, data.inspectionType, data.inspectionSummary])

  return (
    <Page size='A4' style={PDFStyles.page} id='cover' wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'cover', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* API 570 External Checklist Header */}
        <SectionHeader
          title={
            data.inspectionType
              ? `${data.inspectionType} Checklist`
              : 'API 570 External Checklist'
          }
        />

        {/* Inspection Summary Section */}
        <View style={{ marginBottom: 20 }}>
          <View
            style={{
              flexDirection: 'row',
              marginBottom: 10,
            }}>
            <Text style={{ fontSize: 11, fontWeight: 700, width: 150 }}>
              Inspection Summary
            </Text>
            <View style={{ flex: 1 }}>
              {inspectionSummary && (
                <>
                  <Text style={{ fontSize: 10, marginBottom: 5 }}>
                    COMPANY: {inspectionSummary.company}
                  </Text>
                  <Text style={{ fontSize: 10, marginBottom: 5 }}>
                    INSPECTION: {inspectionSummary.inspectionType}
                  </Text>
                  <Text style={{ fontSize: 10, marginBottom: 5 }}>
                    CIRCUIT: {data.circuitId || 'N/A'}
                  </Text>
                  <Text style={{ fontSize: 10, marginBottom: 5 }}>
                    API 570: PERKINS, ELIJAH
                  </Text>
                </>
              )}
              <Text style={{ fontSize: 10, marginBottom: 10, marginTop: 10 }}>
                {inspectionSummary?.summary || ''}
              </Text>
            </View>
          </View>
        </View>

        {/* Completion Date */}
        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: 700, width: 150 }}>
            Completion Date
          </Text>
          <Text style={{ fontSize: 10 }}>
            {inspectionSummary?.completionDate || formatDate(dayjs())}
          </Text>
        </View>

        {/* Asset ID */}
        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: 700, width: 150 }}>
            Asset ID
          </Text>
          <Text style={{ fontSize: 10 }}>{data.assetId || data.asset}</Text>
        </View>

        {/* Degradation Mechanism */}
        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: 700, width: 150 }}>
            Degradation Mechanism
          </Text>
          <Text style={{ fontSize: 10 }}>
            {data.degradationMechanism || 'N/A'}
          </Text>
        </View>

        {/* Type of Inspection */}
        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: 700, width: 150 }}>
            Type of Inspection
          </Text>
          <Text style={{ fontSize: 10 }}>{data.inspectionType}</Text>
        </View>

        {/* Extent */}
        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: 700, width: 150 }}>
            Extent
          </Text>
          <Text style={{ fontSize: 10 }}>{data.extent || 'N/A'}</Text>
        </View>

        {/* Actual Work Time */}
        <View style={{ flexDirection: 'row', marginBottom: 20 }}>
          <Text style={{ fontSize: 11, fontWeight: 700, width: 150 }}>
            Actual Work Time
          </Text>
          <Text style={{ fontSize: 10 }}>{data.actualWorkTime || 'N/A'}</Text>
        </View>

        {/* Signatures Section */}
        <View style={{ marginTop: 'auto', paddingTop: 30 }}>
          <Text style={{ fontSize: 11, fontWeight: 700, marginBottom: 10 }}>
            Signatures
          </Text>

          <View
            style={{
              flexDirection: 'row',
              borderBottom: '1 solid #E5E7EB',
              paddingBottom: 10,
              marginBottom: 10,
            }}>
            <View style={{ flex: 1 }}>
              {signoff?.supervisorSignoff ? (
                <Image
                  src={signoff.supervisorSignoff.signature}
                  style={{ height: 40, width: 120 }}
                />
              ) : (
                <View style={{ height: 40 }} />
              )}
              <Text style={{ fontSize: 10, marginTop: 5 }}>
                {signoff?.supervisorSignoff?.name || ''}
              </Text>
              <Text style={{ fontSize: 9, color: '#6B7280' }}>
                {signoff?.supervisorSignoff?.qualifications || ''}
              </Text>
            </View>

            <View style={{ width: 150 }}>
              <Text style={{ fontSize: 10, color: '#6B7280' }}>
                {signoff?.supervisorSignoff
                  ? formatDate(signoff.supervisorSignoff.timestamp)
                  : ''}
              </Text>
            </View>
          </View>

          <View
            style={{
              flexDirection: 'row',
              paddingBottom: 10,
            }}>
            <View style={{ flex: 1 }}>
              {signoff?.reviewerSignoff ? (
                <Image
                  src={signoff.reviewerSignoff.signature}
                  style={{ height: 40, width: 120 }}
                />
              ) : (
                <View style={{ height: 40 }} />
              )}
              <Text style={{ fontSize: 10, marginTop: 5 }}>
                {signoff?.reviewerSignoff?.name || ''}
              </Text>
              <Text style={{ fontSize: 9, color: '#6B7280' }}>
                {signoff?.reviewerSignoff?.qualifications || ''}
              </Text>
            </View>

            <View style={{ width: 150 }}>
              <Text style={{ fontSize: 10, color: '#6B7280' }}>
                {signoff?.reviewerSignoff
                  ? formatDate(signoff.reviewerSignoff.timestamp)
                  : ''}
              </Text>
            </View>
          </View>
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<CoverSectionData>()
  const {
    formData,
    handleCoverSectionFinish,
    hasCantileverData,
    prePopulatedData,
  } = useVisualReportOps()

  const onFinish = useCallback(
    async (values: CoverSectionData) => {
      try {
        await handleCoverSectionFinish(values)
        message.success('Cover page saved successfully!')
      } catch {
        message.error('Failed to save cover page')
      }
    },
    [handleCoverSectionFinish]
  )

  const initialValues = React.useMemo(
    () => ({
      ...formData.coverSectionData,
      ...prePopulatedData,
    }),
    [formData.coverSectionData, prePopulatedData]
  )

  const inspectionTypeOptions = [
    { label: 'API 570 External Checklist', value: 'API 570 External' },
    { label: 'API 510 External Checklist', value: 'API 510 External' },
    { label: 'API 653 External Checklist', value: 'API 653 External' },
    { label: 'Visual Inspection', value: 'Visual Inspection' },
    { label: 'External Visual', value: 'External Visual' },
  ]

  return (
    <StyledForm<CoverSectionData>
      form={form}
      name='coverSectionForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        name='asset'
        label='Asset'
        rules={[{ required: true, message: 'Please enter asset name' }]}>
        <Input disabled={hasCantileverData} />
      </Form.Item>

      <Form.Item
        name='assetId'
        label='Asset ID'
        rules={[{ required: true, message: 'Please enter asset ID' }]}>
        <Input disabled={hasCantileverData} />
      </Form.Item>

      <Form.Item name='circuitId' label='Circuit ID'>
        <Input />
      </Form.Item>

      <Form.Item
        name='inspectionType'
        label='Inspection Type'
        rules={[{ required: true, message: 'Please select inspection type' }]}>
        <Select options={inspectionTypeOptions} />
      </Form.Item>

      <Form.Item name='inspectionSummary' label='Inspection Summary'>
        <Input.TextArea rows={3} />
      </Form.Item>

      <Form.Item name='degradationMechanism' label='Degradation Mechanism'>
        <Input.TextArea rows={3} />
      </Form.Item>

      <Form.Item name='extent' label='Extent'>
        <Input.TextArea
          rows={4}
          placeholder='Describe the extent of the inspection...'
        />
      </Form.Item>

      <Form.Item name='actualWorkTime' label='Actual Work Time'>
        <Input placeholder='e.g., 4 hours' />
      </Form.Item>

      {/* Display read-only fields for personnel info from signoff */}
      {formData.signoff && (
        <>
          <Form.Item label='Field Technician'>
            <Input
              value={`${formData.signoff.supervisorSignoff?.name || ''} - ${formData.signoff.supervisorSignoff?.qualifications || ''}`}
              disabled
            />
          </Form.Item>

          <Form.Item label='Reviewer'>
            <Input
              value={`${formData.signoff.reviewerSignoff?.name || ''} - ${formData.signoff.reviewerSignoff?.qualifications || ''}`}
              disabled
            />
          </Form.Item>
        </>
      )}

      {/* Display read-only field for revision information */}
      {formData.revisionMetadata && (
        <Form.Item label='Revision Number'>
          <Input value={formData.revisionMetadata.revisionNumber} disabled />
        </Form.Item>
      )}

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Cover Page
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const CoverSection: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: 'cover',
  description: 'Basic inspection information and metadata',
  title: 'Cover Page',
  requiredFields: requiredCoverFields,
}

export const CoverSectionPDF = RenderPDF
